/**
 * Types TypeScript espelhando os schemas Pydantic da API (assets/api/schemas.py).
 * Mantenha sincronizado quando o backend mudar.
 */

/** Alerta do gap-detector (reconciliador DataJud × DJEN). */
export interface LoginEvento {
  id: number;
  usuario_id: number | null;
  email: string | null;
  criado_em: string;
  ip: string | null;
  metodo: string | null;        // 'senha' | 'oauth' | 'signup'
  cidade: string | null;
  pais: string | null;
  lat: number | null;
  lon: number | null;
  geo_ok: number;
  anomalia: number;             // 1 = viagem implausível
  veredito: string | null;      // 'ok' | 'sem_geo' | 'flag' | 'block' | 'erro'
  velocidade_kmh: number | null;
  distancia_km: number | null;
  janela_min: number | null;
  session_id: string | null;
  detalhe: string | null;
}

export interface AlertaReconc {
  chave: string;
  numero_processo: string;
  codigo_movimento: number | null;
  nome_movimento: string | null;
  data_movimento: string | null;
  tipo: string;
  status: string;
  detalhe: string | null;
  criado_em: string;
}

export type Etiqueta =
  | 'vermelho'
  | 'amarelo'
  // cores de dono (coluna EM ANDAMENTO) — overhaul v4
  | 'azul_claro'
  | 'azul_escuro'
  | 'roxo'
  | 'laranja'
  | 'rosa'
  | 'teal'
  | 'lavanda'
  | 'verde'
  | 'cinza';

export type ColunaKanban =
  | 'nao_feitos'
  | 'em_andamento'
  | 'pronto_protocolo'
  | 'concluidos';

/** Etapa MACRO do pipeline DOCAS — dirige as sub-colunas de "Em andamento". */
export type EtapaPipeline =
  | 'fila'
  | 'preparo'
  | 'mapa'
  | 'mapa_aprovado'
  | 'redigindo'
  | 'revisao'
  | 'concluido';

// Modelo de 2 papéis (overhaul v4): admin (Marcelo+Jorge) | colaborador (Maria+demais).
// Aliases legados mantidos só para tolerar respostas antigas em cache.
export type Papel = 'admin' | 'gerente' | 'colaborador' | 'testador' | 'audiencista' | 'correspondente' | 'triador' | 'protocolador';

export type CorEtiqueta =
  | 'azul_claro'
  | 'azul_escuro'
  | 'roxo'
  | 'laranja'
  | 'rosa'
  | 'teal'
  | 'lavanda'
  | null;

export type NomeTransicao =
  | 'triar'
  | 'devolver_triagem'
  | 'assumir'                      // genérico (overhaul v4) — cor vem do usuário
  | 'marcar_pronto'
  | 'protocolar'
  // Fase 4.5 — undo granular (vindos do GET /transicoes-permitidas)
  | 'desfazer_triagem'
  | 'desfazer_assumir'
  | 'desfazer_pronto'             // genérico — backend resolve cor do dono
  | 'desfazer_protocolo';

// Versão amigável dos endpoints (URL path)
export type EndpointTransicao =
  | 'triar'
  | 'devolver-triagem'
  | 'assumir'
  | 'marcar-pronto'
  | 'protocolar'
  | 'desfazer-triagem'
  | 'desfazer-assumir'
  | 'desfazer-pronto'
  | 'desfazer-protocolo';

export interface EscritorioOut {
  id: number;
  nome: string;
  oab_numero: string | null;
  oab_uf: string | null;
  plano: 'free' | 'pro';
  claude_conectado: boolean;
}

export interface UsuarioOut {
  id: number;
  nome: string;
  email: string;
  papel: Papel;
  cor_etiqueta: CorEtiqueta;
  ativo: boolean;
  /** Só vem true em /api/me para o gestor de usuários (controla a tela 👥 Usuários). */
  pode_gerir_usuarios?: boolean;
  /** Super usuário — acesso irrestrito + painel Gestão de Escritórios. */
  super_admin?: boolean;
  oab_numero?: string | null;
  oab_uf?: string | null;
  escritorio_id?: number;
  /** Só vem em /api/me — workspace + plano (freemium). */
  escritorio?: EscritorioOut | null;
  /** Só vem em /api/me — capacidades efetivas (papel × tenant) p/ <Pode>/menu. */
  capacidades?: string[];
}

export interface PrazoCard {
  id_prazo: number;
  oab_origem: string | null;        // OAB que recebeu a intimação ("123456/RJ")
  numero_processo: string;
  titulo?: string | null;           // rótulo do caso (petição inicial, sem CNJ)
  origem?: string | null;           // 'djen'|'manual'|'pauta'|'peticao_inicial'…
  sigla_tribunal: string | null;
  tipo_prazo: string;
  data_vencimento: string;          // ISO date
  data_evento: string | null;
  hora_evento: string | null;
  dias_uteis: number;
  etiqueta: Etiqueta;
  coluna: ColunaKanban;
  atribuido_a: UsuarioOut | null;
  triado_por: UsuarioOut | null;
  triado_em: string | null;          // ISO datetime
  pronto_em: string | null;
  protocolado_por: UsuarioOut | null;
  protocolado_em: string | null;
  tem_peticao_drive: boolean;
  /** Habilita "Assumir": PDF no Drive OU cópia local no servidor. */
  tem_pdf?: boolean;
  /** Pipeline DOCAS — etapa macro atual (null = ainda não iniciado). */
  etapa_pipeline: EtapaPipeline | null;
  /** Status de geração de peças (espelho de pecas_geracoes.status). */
  pecas_status: StatusPecas | null;
  /** `key` do TIPOS_PECA (tiposPeca.ts) escolhido — espelho de pecas_geracoes.tipo_peca. */
  tipo_peca: string | null;
  /** Cards por processo — id do agrupador (null = card avulso, ex.: petição inicial). */
  processo_id?: number | null;
  /** Nº de prazos ABERTOS do mesmo processo (1 = só este; >1 → badge "+N serviços"). */
  servicos_total?: number;
  dias_ate_vencimento: number;
}

export interface TriagemHaikuOut {
  tipo_intimacao: string | null;
  urgencia: 'alta' | 'media' | 'baixa' | null;
  acao_sugerida: string | null;
  resumo: string | null;
  prazo_em_dobro: boolean;
  polo_intimado: string | null;
  alertas: string[];
  data_audiencia: string | null;
  hora_audiencia: string | null;
}

export interface ArquivoDrive {
  id: string;
  nome: string;
  mime_type: string;
  link: string | null;
  tamanho_bytes: number | null;
  criado_em: string | null;  // ISO datetime
}

export interface PrazoDetalhe extends PrazoCard {
  data_disponibilizacao: string | null;
  data_publicacao: string | null;
  data_inicio: string;
  tipo_evento: string | null;      // 'vencimento' | 'audiencia'
  observacao: string | null;       // nota manual (editável no "Ajustar")
  teor_resumo: string | null;
  link_documento: string | null;
  meio: string | null;
  drive_folder_id: string | null;
  drive_peticao_id: string | null;
  drive_folder_link: string | null;
  arquivos_drive: ArquivoDrive[];
  triagem: TriagemHaikuOut | null;
  datajud_ultimo_mov: string | null;
  datajud_data_mov: string | null;
  datajud_em: string | null;
}

export interface UploadResponse {
  id_prazo: number;
  arquivo: ArquivoDrive;
  drive_folder_id: string;
  drive_folder_link: string | null;
  aplicada_por: UsuarioOut;
  timestamp: string;
  arquivos_drive: ArquivoDrive[];
}

export interface DriveStatus {
  configurado: boolean;
  motivo: string | null;
  email_oauth: string | null;
  raiz_folder_id: string | null;
  // Fase 0 — saúde do token p/ a UI avisar antes de quebrar
  expira_em: string | null;
  precisa_reauth: boolean;
  tem_refresh_token: boolean;
}

export interface ColunaCards {
  coluna: ColunaKanban;
  titulo: string;
  total: number;
  cards: PrazoCard[];
}

export interface KanbanSemana {
  semana_iso: string;
  inicio: string;
  fim: string;
  colunas: ColunaCards[];
  total: number;
}

export interface EventoAudit {
  id: number;
  id_prazo: number;
  usuario_id: number | null;
  usuario_nome: string | null;
  de: Etiqueta | null;
  para: Etiqueta;
  observacao: string | null;
  timestamp: string;
}

export interface ContagemEtiqueta {
  vermelho: number;
  amarelo: number;
  azul_claro: number;
  azul_escuro: number;
  verde: number;
  cinza: number;
  total: number;
}

export interface GCalSyncStatus {
  ok: boolean;
  event_id: string | null;
  motivo: string | null;
  html_link: string | null;
}

export interface TransicaoResponse {
  id_prazo: number;
  transicao: NomeTransicao;
  etiqueta_anterior: Etiqueta;
  etiqueta_nova: Etiqueta;
  aplicada_por: UsuarioOut;
  timestamp: string;
  gcal_sync: GCalSyncStatus;
  card: PrazoCard;
}

export interface TransicoesPermitidas {
  id_prazo: number;
  etiqueta_atual: Etiqueta;
  transicoes: NomeTransicao[];
}

export interface HealthOut {
  ok: boolean;
  versao: string;
  db_ok: boolean;
  usuarios_seed_ok: boolean;
  timestamp: string;
}

// ─── helpers de UI ────────────────────────────────────────────────────────

export const ROTULOS_ETIQUETA: Record<Etiqueta, string> = {
  vermelho:    'Não triado',
  amarelo:     'Triado',
  azul_claro:  'Em andamento',
  azul_escuro: 'Em andamento',
  roxo:        'Em andamento',
  laranja:     'Em andamento',
  rosa:        'Em andamento',
  teal:        'Em andamento',
  lavanda:     'Em andamento',
  verde:       '✓ Pronto p/ protocolo',
  cinza:       '✓ Protocolado',
};

// Rótulo amigável de cada cor de dono (usado na tela de Usuários).
export const ROTULOS_COR: Record<NonNullable<CorEtiqueta>, string> = {
  azul_claro:  'Azul claro',
  azul_escuro: 'Azul escuro',
  roxo:        'Roxo',
  laranja:     'Laranja',
  rosa:        'Rosa',
  teal:        'Teal',
  lavanda:     'Lavanda',
};

export const TITULOS_COLUNA: Record<ColunaKanban, string> = {
  nao_feitos:       '📌 TRIAGEM INICIAL',
  em_andamento:     '📝 EM ANDAMENTO',
  pronto_protocolo: 'PRONTO P/ PROTOCOLO',
  concluidos:       'CONCLUÍDOS',
};

export type MockLogin = 'marcelo' | 'jorge' | 'maria';

/** Sessão do usuário autenticado por senha (espelho local p/ UI; o gate real é o cookie JWT). */
export interface SessaoUsuario {
  id: number;
  nome: string;
  papel: string;
  precisa_trocar_senha: boolean;
}


// ─── Peças processuais (Fase 9) ───────────────────────────────────────────

export type StatusPecas =
  | 'sem_pdf'
  | 'pendente'
  | 'gerando'
  | 'pronto'
  | 'erro'
  | 'pendente_refinamento';

export interface PecasStatus {
  id_prazo: number;
  status: StatusPecas;
  mapa_drive_id: string | null;
  mapa_url: string | null;
  rascunho_drive_id: string | null;
  rascunho_url: string | null;
  docx_drive_id: string | null;
  docx_url: string | null;
  rascunho_versao: number;
  /** Versão do .docx publicado no Drive. Se < rascunho_versao (ou null) o docx
   *  está desatualizado — a UI desabilita "Exibir petição" até "Regerar petição". */
  docx_versao: number | null;
  tipo_peca: string | null;
  /** Qual operação está em andamento — define qual botão anima. */
  gerando_tipo: 'mapa' | 'blocos' | 'peticao' | 'ocr' | 'processo' | 'preparo' | 'classify' | 'pack' | 'render' | 'salvando' | null;
  operador_assumiu: string | null;
  feedback_pendente: string | null;
  erro_msg: string | null;
  ultima_atualizacao: string | null;
  /** Pipeline unificado "Mapa do Processo" (Fase 12) — barra de progresso. */
  progresso_etapa: 'split' | 'ocr' | 'pdftotext' | 'classify' | 'pack' | 'mapa' | 'blocos' | 'render' | 'peticao' | 'salvando' | null;
  progresso_pct: number | null;   // 0-100
  progresso_msg: string | null;
  /** Pipeline DOCAS — etapa macro persistente do card. */
  etapa_pipeline: EtapaPipeline | null;
}

// ─── DataJud (API Pública do CNJ — metadados + movimentos) ────────────────
export interface DatajudMovimento {
  data: string | null;   // ISO
  nome: string | null;
  codigo: number | null;
}
export interface DatajudInfo {
  numero: string | null;
  classe: string | null;
  tribunal: string | null;
  grau: string | null;
  vara: string | null;
  data_ajuizamento: string | null;   // 'AAAAMMDDHHMMSS'
  nivel_sigilo: number | null;
  assuntos: string[];
  ultima_atualizacao: string | null;
  n_movimentos: number;
  movimentos: DatajudMovimento[];
}

export interface RefinementRequest {
  feedback: string;
  rascunho_json: object[];
}

export interface RefinementResponse {
  id_prazo: number;
  versao: number;
  rascunho_drive_id: string | null;
  docx_drive_id: string | null;
  timestamp: string;
}


// ─── Overhaul v4: criação manual / CRUD usuários / audiências ──────────────

export interface CriarPrazoIn {
  numero_processo: string;
  tipo_prazo: string;
  data_vencimento: string;      // ISO date YYYY-MM-DD
  sigla_tribunal?: string | null;
  dias_uteis?: number;
  observacao?: string | null;
  /** Token de staging (upload de autos já processado) — pula a Triagem. */
  staging_token?: string | null;
  /** `key` do TIPOS_PECA escolhida no popup "Novo Prazo". */
  tipo_peca_escolhida?: string | null;
  /** Cards por processo — novo serviço num card existente. */
  processo_id?: number | null;
  /** 'manter' reaproveita o autos.pdf atual (sem upload); 'substituir' exige staging_token. */
  pdf_acao?: 'manter' | 'substituir' | null;
}

// ─── Cards por processo (busca antes de criar — modelo perícias) ───────────

export interface ProcessoBusca {
  processo_id: number;
  numero_processo: string;
  sigla_tribunal: string | null;
  pdf_nome: string | null;
  pdf_atualizado_em: string | null;
  tem_pdf: boolean;
  servicos_total: number;
  servicos: PrazoCard[];
}

// ─── Staging (upload de autos ANTES de existir o prazo/card) ──────────────

export interface StagingUploadOut {
  token: string;
}

export interface StagingStatusOut {
  token: string;
  status: 'processando' | 'pronto' | 'erro';
  progresso_etapa: string | null;
  progresso_pct: number;
  progresso_msg: string | null;
  numero_processo_sugerido: string | null;
  tribunal_sugerido: string | null;
  tipo_peca_sugerido: string | null;
  vencimento_sugerido: string | null;
  /** Derivação do vencimento: data-base (publicação mais recente, ISO) + dias úteis. */
  vencimento_base: string | null;
  vencimento_dias_uteis: number | null;
  /** Fonte da sugestão: 'djen' (ao vivo) | 'certidao' (PDF) | 'texto'. */
  vencimento_fonte: string | null;
  erro_msg: string | null;
}

// ─── Peticionar com modelo ────────────────────────────────────────────────

/** Status do modelo .docx de um tipo de peça (GET /api/modelos-peca). */
export interface ModeloPecaStatus {
  rotulo: string;
  tem_modelo: boolean;
  nome: string | null;
  atualizado_em: string | null;
}

/** Um campo canônico do contexto do processo (autopreenchimento). */
export interface CampoPeticao {
  key: string;
  label: string;
  valor: string;
  source: 'autos' | 'cliente' | 'datajud' | null;
  manual_provavel: boolean;
}

/** GET /api/peticionar/{id}/dados — dados básicos do processo. */
export interface DadosPeticao {
  id_prazo: number;
  numero_processo: string;
  contexto: Record<string, string>;
  campos: CampoPeticao[];
}

/** Um placeholder classificado na conferência do template. */
export interface PlaceholderItem {
  name: string;
  count: number;
  label?: string | null;
  suggestion?: string | null;
}

/** Resultado da conferência de placeholders (porta do gpValidate). */
export interface ConferenciaPlaceholders {
  ok: PlaceholderItem[];
  empty: PlaceholderItem[];
  unknown: PlaceholderItem[];
  missing: PlaceholderItem[];
  if_count: number;
  endif_count: number;
  if_balance: boolean;
  pedidos_ref: number[];
  other_jinja: string[];
  total_placeholders: number;
  erro?: string;
}

/** POST /api/peticionar/{id}/conferir — conferência + contexto. */
export interface ConferirResponse {
  id_prazo: number;
  piece_key: string;
  rotulo: string;
  modelo_nome: string | null;
  contexto: Record<string, string>;
  campos: CampoPeticao[];
  conferencia: ConferenciaPlaceholders;
}

/** POST /api/peticionar/{id}/gerar — petição renderizada salva no Drive. */
export interface GerarPeticaoResponse {
  id_prazo: number;
  piece_key: string;
  rotulo: string;
  nome: string;
  docx_drive_id: string;
  docx_url: string | null;
}

/** Item do registry de escritórios (seletor de impersonação do super usuário). */
export interface EscritorioRegistro {
  id: number;
  nome: string;
  plano?: string;
  ativo?: number | boolean;
}

/** Body de PATCH /api/prazos/{id} — "Ajustar" (só campos presentes mudam). */
export interface EditarPrazoIn {
  numero_processo?: string;
  sigla_tribunal?: string | null;
  tipo_prazo?: string;
  data_vencimento?: string;     // ISO YYYY-MM-DD
  data_inicio?: string;         // ISO YYYY-MM-DD
  dias_uteis?: number;
  tipo_evento?: 'vencimento' | 'audiencia';
  data_evento?: string | null;  // ISO YYYY-MM-DD
  hora_evento?: string;         // HH:MM
  titulo?: string | null;
  observacao?: string | null;
}

export interface CriarCasoIn {
  titulo: string;                // rótulo do caso, ex.: "Fulano × Empresa Y"
  sigla_tribunal?: string | null;
  data_alvo?: string | null;     // ISO date YYYY-MM-DD (referência opcional)
}

export interface CriarUsuarioIn {
  nome: string;
  email: string;
  papel: Papel;
  cor_etiqueta?: CorEtiqueta;
}

export interface AtualizarUsuarioIn {
  papel?: Papel;
  cor_etiqueta?: CorEtiqueta;
  ativo?: boolean;
  email?: string;
  oab_numero?: string;
  oab_uf?: string;
  super_admin?: boolean;
}

export interface AudienciaItem {
  id_prazo: number;
  numero_processo: string;
  sigla_tribunal: string | null;
  tipo_prazo: string;
  data_audiencia: string | null;   // ISO date — null = agendar manual
  hora_audiencia: string | null;
  origem: 'evento' | 'triagem';
  etiqueta: Etiqueta | null;
  coluna: ColunaKanban | null;
  status: string;
  teor_resumo: string | null;
}

export interface AgendarAudienciaIn {
  data_audiencia: string;          // ISO date
  hora_audiencia: string;          // HH:MM
}

export interface CriarAudienciaIn {
  numero_processo: string;
  data_audiencia: string;          // ISO date YYYY-MM-DD
  hora_audiencia?: string;         // HH:MM (default 09:00)
  tipo_prazo?: string;             // default "Audiência Designada"
  sigla_tribunal?: string | null;
  observacao?: string | null;
}

// ─── Financeiro (aba admin-only) ───────────────────────────────────────────

export type StatusReceita = 'previsto' | 'parcial' | 'recebido';
export type FormaRecebimento = 'pix' | 'ted' | 'deposito' | 'dinheiro' | 'cheque' | 'outro';

export interface ConfigFinanceiro {
  escritorio_id: number;
  pct_contratuais_padrao: number;
  atualizado_em: string | null;
}

export interface Socio {
  id: number;
  nome: string;
  usuario_id: number | null;
  pct_contratuais: number;
  pct_sucumbencia: number;
  ativo: boolean;
  criado_em?: string | null;
  atualizado_em?: string | null;
}
export interface SocioIn {
  nome: string;
  usuario_id?: number | null;
  pct_contratuais: number;
  pct_sucumbencia: number;
  ativo?: boolean;
}

export interface DivisaoSocio {
  id?: number;
  socio_id: number;
  socio_nome: string | null;
  pct_contratuais: number;
  valor_contratuais: number;
  pct_sucumbencia: number;
  valor_sucumbencia: number;
  valor: number;
}
export interface DivisaoItemIn {
  socio_id: number;
  pct_contratuais: number;
  pct_sucumbencia: number;
}

export interface Receita {
  id: number;
  numero_processo: string;
  cliente_nome: string | null;
  advogado_responsavel: string | null;
  valor_base: number;
  pct_contratuais: number;
  valor_contratuais: number;
  valor_sucumbencia: number;
  valor_total: number;
  status: StatusReceita;
  valor_recebido: number;
  data_prevista: string | null;
  data_recebimento: string | null;
  forma_recebimento: FormaRecebimento | null;
  observacao: string | null;
  criado_em?: string | null;
  atualizado_em?: string | null;
  divisao?: DivisaoSocio[];
}
export interface ReceitaIn {
  numero_processo: string;
  cliente_nome?: string | null;
  advogado_responsavel?: string | null;
  valor_base: number;
  pct_contratuais: number;
  valor_sucumbencia: number;
  status: StatusReceita;
  valor_recebido?: number;
  data_prevista?: string | null;
  data_recebimento?: string | null;
  forma_recebimento?: FormaRecebimento | null;
  observacao?: string | null;
  divisao?: DivisaoItemIn[] | null;
}
export type ReceitaUpdateIn = Partial<ReceitaIn>;

export interface ProcessoPrefill {
  numero_processo: string;
  numero_processo_raw: string;
  cliente_nome: string | null;
  encontrado: boolean;
  ja_tem_receita: boolean;
}

export interface FinReceitaFiltros {
  status?: StatusReceita | '';
  numero_processo?: string;
  data_ini?: string;
  data_fim?: string;
}

export interface DashboardFinanceiro {
  kpis: {
    total_receita: number; total_contratuais: number; total_sucumbencia: number;
    total_recebido: number; total_a_receber: number; qtd_processos: number;
    qtd_previsto: number; qtd_parcial: number; qtd_recebido: number; ticket_medio: number;
  };
  por_socio: { socio_id: number; socio_nome: string; total: number; contratuais: number; sucumbencia: number; qtd_processos: number }[];
  por_cliente: { cliente_nome: string; total: number; recebido: number; a_receber: number; qtd: number }[];
  por_mes: { mes: string; recebido: number; qtd: number }[];
  contratuais_sucumbencia: { contratuais: number; sucumbencia: number };
  aging: { a_vencer: number; f0_30: number; f31_60: number; f61_90: number; f90_mais: number; vencido: number; total: number };
  aviso_pct_socios: boolean;
}

// ── Monitor de gasto da assinatura (uso de IA por call → R$) ──────────────────
export interface UsoIaEvento {
  id: number;
  escritorio_id: number;
  ts: string;
  etapa: string | null;        // mapa | blocos | triagem | refino | peca
  id_prazo: number | null;
  input_tokens: number;
  output_tokens: number;
  cache_tokens: number;
  custo_usd: number;           // equivalente-API (total_cost_usd)
  valor_centavos: number;      // R$ amortizado da assinatura
  meta: string | null;
}

export interface CustosPainel {
  periodo: { de: string; ate: string };
  escopo: 'global' | 'tenant';
  config: {
    assinatura_custo_mensal_centavos: number;
    assinatura_tokens_dia: number;
    assinatura_tokens_semana: number;
    teto_mensal_tokens: number;
    restricao: '' | 'diaria' | 'semanal';
  };
  kpis: {
    n_calls: number;
    custo_usd_total: number;
    valor_centavos_total: number;
    pct_assinatura: number;
    tokens_total: number;
    valor_centavos_por_call: number;
    projecao_centavos_mes: number;
  };
  por_etapa: { etapa: string | null; calls: number; tokens: number; custo_usd: number; valor_centavos: number }[];
  serie: { dia: string; valor_centavos: number; custo_usd: number; calls: number }[];
  ranking: { escritorio_id: number; valor_centavos: number; custo_usd: number; calls: number; nome: string }[];
}

// custo_config é gravado como TEXT → valores chegam como string.
export interface CustosConfig {
  assinatura_custo_mensal_centavos: string;
  assinatura_tokens_dia: string;
  assinatura_tokens_semana: string;
}
